import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { Upload, FileSpreadsheet, CheckCircle, Loader2, Calendar as CalendarIcon, Home, CheckSquare, Square, FileSignature, Settings2, Truck, Search, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoiceCreateDomesticPage = () => {
  const [file, setFile] = useState(null);
  const [customDate, setCustomDate] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [result, setResult] = useState({ invoiceCount: 0, overweightCount: 0 });
  
  const [drafts, setDrafts] = useState([]);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editPriceValue, setEditPriceValue] = useState("");
  const [editingWeightId, setEditingWeightId] = useState(null);
  const [editWeightValue, setEditWeightValue] = useState("");
  const [isSavingField, setIsSavingField] = useState(false);
  const formatPriceInput = (val) => {
    if (val === "" || val === null || val === undefined) return "";
    return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };
  const parsePriceInput = (val) => {
    const clean = val.replace(/\./g, "");
    return clean === "" ? 0 : parseFloat(clean);
  };
  const startEditPrice = (draft, e) => {
    e.stopPropagation();
    setEditingPriceId(draft.id);
    setEditPriceValue(formatPriceInput(draft.base_price || 0));
  };
  const cancelEditPrice = (e) => { if (e) e.stopPropagation(); setEditingPriceId(null); setEditPriceValue(""); };
  const saveEditPrice = async (draftId, e) => {
    if (e) e.stopPropagation();
    const newPrice = parsePriceInput(editPriceValue);
    if (newPrice < 0) { toast.error("Harga tidak boleh negatif"); return; }
    setIsSavingField(true);
    try {
      await axios.put(`${API}/invoices/draft/${draftId}/price`, { base_price: newPrice });
      setDrafts(prev => prev.map(d => d.id === draftId ? { ...d, base_price: newPrice } : d));
      toast.success("Harga berhasil diperbarui");
      setEditingPriceId(null); setEditPriceValue("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memperbarui harga");
    } finally { setIsSavingField(false); }
  };
  const startEditWeight = (draft, e) => {
    e.stopPropagation();
    setEditingWeightId(draft.id);
    setEditWeightValue(String(draft.weight_kg ?? ""));
  };
  const cancelEditWeight = (e) => { if (e) e.stopPropagation(); setEditingWeightId(null); setEditWeightValue(""); };
  const saveEditWeight = async (draftId, e) => {
    if (e) e.stopPropagation();
    const newWeight = parseFloat(editWeightValue.replace(',', '.'));
    if (isNaN(newWeight) || newWeight < 0) { toast.error("Weight tidak valid"); return; }
    setIsSavingField(true);
    try {
      await axios.put(`${API}/invoices/draft/${draftId}/weight`, { weight_kg: newWeight });
      setDrafts(prev => prev.map(d => d.id === draftId ? { ...d, weight_kg: newWeight } : d));
      toast.success("Weight berhasil diperbarui");
      setEditingWeightId(null); setEditWeightValue("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memperbarui weight");
    } finally { setIsSavingField(false); }
  };
  const handleDeleteDraft = async (draftId, awb, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Hapus draft AWB ${awb}? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await axios.delete(`${API}/invoices/draft/${draftId}`);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      toast.success(`Draft AWB ${awb} berhasil dihapus`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menghapus draft");
    }
  };
  const [selectedDrafts, setSelectedDrafts] = useState([]);
  const [isSplitAttn, setIsSplitAttn] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // STATE BARU UNTUK FILTER PENCARIAN
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    fetchDrafts();
  }, []);

  const fetchDrafts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/draft/domestic`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // FILTER KHUSUS DOMESTIK SAJA (FSC = False)
      const sortedData = response.data.sort((a, b) => b.id - a.id);
      setDrafts(sortedData);
    } catch (error) {
      console.error("Gagal menarik data draft", error);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setUploadSuccess(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    multiple: false,
  });

  const handleUpload = async () => {
    if (!customDate) return toast.error('Tanggal Cetak Invoice wajib diisi!');
    if (!file) return toast.error('Pilih file Excel terlebih dahulu');

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('custom_date', customDate);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/invoices/upload/domestic`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult({
        invoiceCount: response.data.invoice_count,
        overweightCount: response.data.overweight_count
      });
      toast.success('Upload data Domestik berhasil!');
      setUploadSuccess(true);
      setFile(null);
      fetchDrafts(); 
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload gagal. Pastikan file sesuai.');
    } finally {
      setUploading(false);
    }
  };

  // LOGIKA PENCARIAN & FILTERING DATA
  const filteredDrafts = useMemo(() => {
    if (!searchQuery) return drafts;
    const lowerQuery = searchQuery.toLowerCase();
    
    return drafts.filter(draft => {
      const awbMatch = draft.awb?.toLowerCase().includes(lowerQuery);
      const custIdMatch = draft.customer_id?.toLowerCase().includes(lowerQuery);
      const custNameMatch = draft.customer_name?.toLowerCase().includes(lowerQuery);
      
      return awbMatch || custIdMatch || custNameMatch;
    });
  }, [drafts, searchQuery]);

  const toggleSelect = (id) => {
    if (selectedDrafts.includes(id)) {
      setSelectedDrafts(selectedDrafts.filter(draftId => draftId !== id));
    } else {
      setSelectedDrafts([...selectedDrafts, id]);
    }
  };

  const toggleSelectAll = () => {
    // Select All sekarang hanya men-select data yang sedang TERFILTER di layar
    if (selectedDrafts.length === filteredDrafts.length && filteredDrafts.length > 0) {
      setSelectedDrafts([]);
    } else {
      setSelectedDrafts(filteredDrafts.map(d => d.id));
    }
  };

  const handleGenerateInvoice = async () => {
    if (selectedDrafts.length === 0) return toast.error("Pilih minimal 1 resi draft!");
    
    setIsGenerating(true);
    const loadToast = toast.loading("Meracik Invoice Domestik... Mohon tunggu.");
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'Authorization': `Bearer ${token}` } };

      const generatePayload = {
          invoice_type: "D",     
          fsc_percent: 0.0,      
          vat_percent: 1.1,
          invoice_date: customDate
      };

      if (isSplitAttn) {
        const groups = {};
        selectedDrafts.forEach(id => {
          const draft = drafts.find(d => d.id === id);
          if (draft) {
            const key = `${draft.customer_id}_${draft.department || 'NONE'}`;
            if (!groups[key]) groups[key] = [];
            groups[key].push(draft.id);
          }
        });

        for (const key in groups) {
          await axios.post(`${API}/invoices/generate`, {
            ...generatePayload,
            invoice_ids: groups[key]
          }, config);
        }
      } else {
        await axios.post(`${API}/invoices/generate`, {
            ...generatePayload,
            invoice_ids: selectedDrafts
        }, config);
      }

      toast.success("Berhasil! Draft telah disahkan menjadi Invoice Domestik (D-).");
      setSelectedDrafts([]);
      fetchDrafts(); 
    } catch (error) {
      toast.error("Gagal membuat Invoice.");
    } finally {
      setIsGenerating(false);
      toast.dismiss(loadToast);
    }
  };

  const expectedHeaders = [
    'CWB', 'Dest', 'Origin', 'TDate', 'CustomerID', 'Name', 
    'NameDest', 'DocType', 'Qty', 'Weight', 'CustSection'
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-6">
      <div>
        <h1 className="text-4xl font-heading font-black mb-2 text-purple-700 tracking-tight">Create Invoice - DOMESTIC</h1>
        <p className="text-gray-500 text-lg">Upload Excel, cek draf tagihan, lalu cetak Invoice Domestik (Tanpa FSC).</p>
      </div>

      <Card className="border-none shadow-[0_8px_40px_rgb(0,0,0,0.06)] rounded-3xl border-t-4 border-t-purple-600 overflow-hidden">
        <CardHeader className="bg-white pb-4">
          <CardTitle className="text-2xl font-heading flex items-center gap-3">
              <Truck className="text-purple-600" size={28}/> Upload AWB Domestic
          </CardTitle>
          <CardDescription className="text-base">Harga ditarik berdasarkan Zona Tujuan (Dest) dengan pembulatan Kg ke atas.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 bg-white">
          
          <div className="bg-purple-50/70 p-5 rounded-2xl border border-purple-200 shadow-inner flex items-center gap-4">
             <div className="bg-purple-100 p-3.5 rounded-xl text-purple-600">
                 <CalendarIcon size={24} />
             </div>
             <div className="flex-1">
                 <label className="block text-sm font-bold text-purple-900 mb-1">
                     Tanggal Cetak Invoice <span className="text-red-500">* (Wajib Diisi)</span>
                 </label>
                 <input 
                    type="date" 
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    className="w-full md:w-64 p-2.5 border-2 border-purple-300 rounded-xl focus:ring-4 focus:ring-purple-500/20 focus:border-purple-600 outline-none transition-all font-bold text-gray-800"
                 />
                 <p className="text-xs text-purple-700 mt-1.5 font-medium">Ini akan menjadi tanggal rilis invoice Anda.</p>
             </div>
          </div>

          <div
            {...getRootProps()}
            className={`border-4 border-dashed rounded-3xl p-12 text-center transition-all duration-300 ${
              isDragActive ? 'border-purple-500 bg-purple-50 scale-[1.02]' : 
              !customDate ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed' : 'border-gray-300 bg-gray-50 hover:border-purple-400 hover:bg-purple-50/30 cursor-pointer'
            }`}
          >
            <input {...getInputProps()} disabled={!customDate} />
            <div className="flex flex-col items-center gap-4">
              {!customDate ? (
                <>
                  <div className="w-24 h-24 rounded-3xl bg-gray-200 flex items-center justify-center">
                    <CalendarIcon className="w-10 h-10 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-500">Pilih Tanggal Dulu!</p>
                    <p className="text-sm font-medium text-red-500 mt-2">Anda harus mengisi tanggal di atas sebelum bisa mengupload file.</p>
                  </div>
                </>
              ) : file ? (
                <>
                  <div className="w-24 h-24 rounded-3xl bg-purple-100 flex items-center justify-center shadow-inner">
                    <FileSpreadsheet className="w-12 h-12 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-800">{file.name}</p>
                    <p className="text-sm font-medium text-gray-500 mt-1">{(file.size / 1024).toFixed(2)} KB</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-24 h-24 rounded-3xl bg-purple-100 flex items-center justify-center shadow-sm">
                    <Upload className="w-12 h-12 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-800">
                      {isDragActive ? 'Lepaskan file disini...' : 'Drag & drop file Excel Domestik disini'}
                    </p>
                    <p className="text-sm font-medium text-gray-500 mt-2">Hanya file .xlsx atau .xls yang diterima</p>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex gap-4 pt-2">
            <Button
              onClick={handleUpload}
              disabled={!file || !customDate || uploading}
              className="flex-1 h-14 rounded-2xl font-bold text-lg bg-purple-600 hover:bg-purple-700 text-white shadow-xl hover:shadow-purple-600/20 transition-all disabled:opacity-50"
            >
              {uploading ? (
                <><Loader2 className="w-6 h-6 mr-3 animate-spin" /> Sedang Menghitung Harga...</>
              ) : (
                <><Upload className="w-6 h-6 mr-3" /> Upload & Hitung Harga Domestik</>
              )}
            </Button>
            {file && (
              <Button onClick={() => setFile(null)} variant="outline" className="h-14 px-8 rounded-2xl font-bold border-2 border-gray-200 hover:bg-gray-100 text-gray-600">
                Clear
              </Button>
            )}
          </div>

          {uploadSuccess && (
            <div className="flex items-center gap-4 p-5 bg-green-50 border-2 border-green-200 rounded-2xl animate-in fade-in zoom-in slide-in-from-bottom-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
              <div className="flex-1">
                <p className="font-black text-green-900 text-lg">Proses Selesai!</p>
                <p className="text-sm text-green-800 mt-1 font-medium">
                  {result.invoiceCount} resi baru telah masuk ke tabel Draft di bawah.<br/>
                  {result.overweightCount > 0 && <span className="text-red-600 font-bold bg-red-100 px-2 py-0.5 rounded mt-1 inline-block">{result.overweightCount} resi tertahan di menu Overweight (>30Kg).</span>}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* --- TABEL DRAFT INVOICE DOMESTIK --- */}
      <div className="space-y-4">
        
        {/* ACTION BAR (DIPINDAH KE ATAS & DITAMBAH SEARCH BAR) */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mt-12 mb-2">
            <div>
                <h2 className="text-2xl font-black text-gray-800 flex items-center gap-2">
                    <FileSignature className="text-purple-600"/> Draft Invoice (Domestic)
                </h2>
                <p className="text-gray-500 text-sm mt-1">
                    Terpilih: <span className="font-black text-purple-700">{selectedDrafts.length}</span> dari {filteredDrafts.length} Resi
                </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                {/* SEARCH INPUT BARU */}
                <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input 
                        type="text" 
                        placeholder="Cari AWB, ID, Nama..." 
                        className="pl-9 w-full p-2.5 border border-gray-300 rounded-xl text-sm focus:border-purple-500 outline-none transition"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                <div className="bg-white p-3 px-5 border border-gray-200 shadow-sm rounded-xl flex items-center gap-3 cursor-pointer hover:bg-purple-50 transition" onClick={() => setIsSplitAttn(!isSplitAttn)}>
                    {isSplitAttn ? <CheckSquare className="text-purple-600" size={22}/> : <Square className="text-gray-300" size={22}/>}
                    <div>
                        <p className="font-bold text-gray-800 text-sm leading-none">Pisah Invoice (Attn)</p>
                    </div>
                </div>

                <Button 
                    onClick={handleGenerateInvoice}
                    disabled={selectedDrafts.length === 0 || isGenerating}
                    className="h-[46px] px-6 bg-gray-900 hover:bg-black text-white font-bold rounded-xl shadow-md transition disabled:opacity-50 w-full sm:w-auto"
                >
                    {isGenerating ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin"/> Proses...</>
                    ) : (
                        <>Generate Invoice <span className="bg-purple-600 text-white text-[10px] px-2 py-0.5 rounded-full ml-2">Awalan D-</span></>
                    )}
                </Button>
            </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-left text-sm whitespace-nowrap relative">
                    <thead className="bg-gray-50 border-b uppercase text-[11px] font-black text-gray-500 tracking-wider sticky top-0 z-10">
                        <tr>
                            <th className="px-5 py-4 w-10 text-center border-r bg-gray-50">
                                <button onClick={toggleSelectAll} className="hover:text-purple-600 transition-colors">
                                    {selectedDrafts.length > 0 && selectedDrafts.length === filteredDrafts.length ? <CheckSquare className="text-purple-600 mx-auto" size={18}/> : <Square className="text-gray-400 mx-auto" size={18}/>}
                                </button>
                            </th>
                            <th className="px-5 py-4 bg-gray-50">AWB & Tgl</th>
                            <th className="px-5 py-4 bg-gray-50">Customer</th>
                            <th className="px-5 py-4 bg-gray-50">Attn / Section</th>
                            <th className="px-5 py-4 text-center bg-gray-50">Weight</th>
                            <th className="px-5 py-4 text-right bg-gray-50">Base Price</th>
                            <th className="px-5 py-4 text-center bg-gray-50">Aksi</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredDrafts.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="p-16 text-center text-gray-400">
                                    <Home className="mx-auto h-12 w-12 text-gray-200 mb-3" />
                                    <p className="font-medium text-base">Belum ada Draft atau tidak ada hasil pencarian.</p>
                                    <p className="text-xs mt-1">Silakan ketik kata kunci lain atau upload Excel baru.</p>
                                </td>
                            </tr>
                        ) : (
                            filteredDrafts.map(draft => {
                                const isSelected = selectedDrafts.includes(draft.id);
                                return (
                                    <tr key={draft.id} className={`hover:bg-purple-50/40 transition-colors ${isSelected ? 'bg-purple-50/80' : ''}`}>
                                        <td className="px-5 py-3 text-center border-r cursor-pointer" onClick={() => toggleSelect(draft.id)}>
                                            {isSelected ? <CheckSquare className="text-purple-600 mx-auto" size={18}/> : <Square className="text-gray-300 mx-auto" size={18}/>}
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="font-bold text-gray-900">{draft.awb}</div>
                                            <div className="text-[11px] text-gray-500 font-mono mt-0.5">{new Date(draft.date).toLocaleDateString()}</div>
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="font-bold text-gray-800 truncate max-w-[200px]">{draft.customer_name}</div>
                                            <div className="text-xs text-gray-500 font-mono mt-0.5">{draft.customer_id}</div>
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="inline-block px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">
                                                {draft.department || '-'}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-center">
                                            {editingWeightId === draft.id ? (
                                                <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                    <input type="text" autoFocus className="w-16 p-1 text-right border border-orange-400 rounded text-sm outline-none"
                                                        value={editWeightValue} onChange={(e) => setEditWeightValue(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') saveEditWeight(draft.id, e); if (e.key === 'Escape') cancelEditWeight(e); }}
                                                        disabled={isSavingField} />
                                                    <span className="text-gray-500 text-xs">Kg</span>
                                                    <button onClick={(e) => saveEditWeight(draft.id, e)} disabled={isSavingField} className="text-green-600 hover:text-green-800 text-xs font-bold px-1">✓</button>
                                                    <button onClick={(e) => cancelEditWeight(e)} disabled={isSavingField} className="text-gray-400 hover:text-red-500 text-xs font-bold px-1">✕</button>
                                                </div>
                                            ) : (
                                                <span className="font-bold text-gray-700 cursor-pointer hover:bg-yellow-50 hover:underline decoration-dotted px-1 rounded" title="Klik untuk edit weight" onClick={(e) => startEditWeight(draft, e)}>
                                                    {draft.weight_kg} Kg
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            {editingPriceId === draft.id ? (
                                                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                                    <span className="text-gray-500 text-xs">Rp</span>
                                                    <input type="text" autoFocus className="w-24 p-1 text-right border border-orange-400 rounded text-sm outline-none"
                                                        value={editPriceValue} onChange={(e) => setEditPriceValue(formatPriceInput(parsePriceInput(e.target.value)))}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') saveEditPrice(draft.id, e); if (e.key === 'Escape') cancelEditPrice(e); }}
                                                        disabled={isSavingField} />
                                                    <button onClick={(e) => saveEditPrice(draft.id, e)} disabled={isSavingField} className="text-green-600 hover:text-green-800 text-xs font-bold px-1">✓</button>
                                                    <button onClick={(e) => cancelEditPrice(e)} disabled={isSavingField} className="text-gray-400 hover:text-red-500 text-xs font-bold px-1">✕</button>
                                                </div>
                                            ) : (
                                                <span className="font-bold font-mono text-gray-900 cursor-pointer hover:bg-yellow-50 hover:underline decoration-dotted px-1 rounded" title="Klik untuk edit harga" onClick={(e) => startEditPrice(draft, e)}>
                                                    Rp {(draft.base_price || 0).toLocaleString('id-ID')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                            <button onClick={(e) => handleDeleteDraft(draft.id, draft.awb, e)} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition" title="Hapus draft ini">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
      </div>

      <Card className="border-none shadow-sm rounded-2xl bg-white border border-gray-100">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold flex items-center gap-2 text-gray-700">
              <Settings2 size={20} className="text-gray-400"/> Format Header Excel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {expectedHeaders.map((header, index) => (
              <div key={index} className="px-3 py-1.5 bg-gray-50 rounded-lg text-xs font-bold border border-gray-200 text-gray-600">
                {header}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoiceCreateDomesticPage;